// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Unit type definitions and enum declarations.
 * @author James Reid
 */

// @ts-check

// @@no-imports

// @@body
/**
 * Length unit abbreviations required within calculator.
 * @typedef {"m"|"mm"|"in"} LengthUnit
 */

/**
 * Area unit abbreviations required within calculator.
 * @typedef {"m2"} AreaUnit
 */

/**
 * Volume unit abbreviations required within calculator.
 * @typedef {"ml"|"L"} VolumeUnit
 */

/**
 * Mass unit abbreviations required within calculator.
 * @typedef {"kg"} MassUnit
 */

/**
 * Custom unit abbreviations required within calculator.
 * @typedef {"SPRAY"|"TANK"|"SINGLE"} CustomUnit
 */

/**
 * Wrapper unit abbreviation typedef for all units *excluding* custom units.
 * @typedef {LengthUnit|AreaUnit|VolumeUnit|MassUnit} Unit
 */

/**
 * Types of unit required within calculator.
 * @typedef {"length"|"area"|"volume"|"mass"} UnitType
 */

// @@exports
/**
 * @type {LengthUnit}
 * @ignore
 */
export let LengthUnit

/**
 * @type {AreaUnit}
 * @ignore
 */
export let AreaUnit

/**
 * @type {VolumeUnit}
 * @ignore
 */
export let VolumeUnit

/**
 * @type {MassUnit}
 * @ignore
 */
export let MassUnit

/**
 * @type {CustomUnit}
 * @ignore
 */
export let CustomUnit

/**
 * @type {Unit}
 * @ignore
 */
export let Unit

/**
 * @type {UnitType}
 * @ignore
 */
export let UnitType

/**
 * Nested units enum containing all available calculator unit abbreviations by
 * long name.
 */
export const UNITS = {
    length: /** @enum {LengthUnit} */ ({
        metre: /** @type {LengthUnit} */ ("m"),
        millimetre: /** @type {LengthUnit} */ ("mm"),
        inch: /** @type {LengthUnit} */ ("in")
    }),
    area: {
        squareMetre: /** @type {AreaUnit} */ ("m2"),
    },
    volume: /** @enum {VolumeUnit} */ ({
        litre: /** @type {VolumeUnit} */ ("L"),
        millilitre: /** @type {VolumeUnit} */ ("ml")
    }),
    mass: /** @enum {MassUnit} */ ({
        kilogram: /** @type {MassUnit} */ ("kg")
    }),
    custom: /** @enum {CustomUnit} */ ({
        spray: /** @type {CustomUnit} */ ("SPRAY"),
        tank: /** @type {CustomUnit} */ ("TANK"),
        single: /** @type {CustomUnit} */ ("SINGLE")
    })
}

/** @enum {UnitType} */
export const UNIT_TYPES = {
    length: /** @type {UnitType} */ ("length"),
    area: /** @type {UnitType} */ ("area"),
    volume: /** @type {UnitType} */ ("volume"),
    mass: /** @type {UnitType} */ ("mass"),
}
